/**
 * InflowPay SDK v2 - Iframe-based Payment SDK
 * 
 * This SDK creates an iframe and communicates with a React payment application
 * using postMessage API for secure cross-origin communication.
 */

import { DIMENSIONS } from './constants/sdk-constants';
import type { IframeMessage, SDKConfig, TransactionData } from './types';
import { LoaderManager } from './ui/loader-manager';
import { ModalManager } from './ui/modal-manager';
import { OverlayManager } from './ui/overlay-manager';
import { getInlineIframeStyles } from './ui/styles';
import { getEnvironmentFromApiKey, getIframeUrlFromApiKey } from './utils';
export class SDK {
  private iframe: HTMLIFrameElement | null = null;
  private iframeUrl: string;
  private config: SDKConfig & { iframeUrl?: string; debug?: boolean };
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private containerElement: HTMLElement | null = null;
  private usePopup: boolean;
  private environment: 'sandbox' | 'production' | 'development' | 'preprod';
  private loaderManager: LoaderManager | null = null;
  private modalManager: ModalManager;

  constructor(config: SDKConfig & { iframeUrl?: string; debug?: boolean }) {
    this.config = config;

    // Auto-detect iframe URL from API key, or use provided iframeUrl (internal use only)
    this.iframeUrl = config.iframeUrl || getIframeUrlFromApiKey(config.publicKey || '');
    this.environment = getEnvironmentFromApiKey(config.publicKey || '');

    // Determine if we should use popup or inline
    this.usePopup = !config.container;

    // Resolve container if provided
    if (config.container) {
      if (typeof config.container === 'string') {
        this.containerElement = document.querySelector(config.container);
        if (!this.containerElement) {
          throw new Error(`Container not found: ${config.container}`);
        }
      } else {
        this.containerElement = config.container;
      }
    }

    // Initialize modal manager
    this.modalManager = new ModalManager({
      environment: this.environment,
      debug: this.config.debug,
    });
  }

  /**
   * Initialize and open the payment iframe
   */
  init(): void {
    if (this.iframe) {
      return;
    }

    this.createIframe();
    this.addMessageListener();
  }

  /**
   * Create and append the iframe to the document
   */
  private createIframe(): void {
    // Build iframe URL with API key and paymentId and locale as query parameters
    const url = new URL(this.iframeUrl);
    if (this.config.publicKey) {
      url.searchParams.set('publicKey', this.config.publicKey);
    }
    if (this.config.config?.paymentId) {
      url.searchParams.set('paymentId', this.config.config.paymentId);
    }
    if (this.config.locale) {
      url.searchParams.set('locale', this.config.locale);
    }
    const iframeSrc = url.toString();

    if (this.usePopup) {
      // Use OverlayManager to create popup UI
      const { overlay, container, iframe } = OverlayManager.createOverlay(
        iframeSrc,
        () => this.close()
      );

      this.iframe = iframe;
      document.body.appendChild(overlay);

      // Show loader
      this.loaderManager = new LoaderManager(container, {
        fillParent: this.config.config?.style?.fillParent,
      });
      this.loaderManager.show(this.iframe);
    } else {
      // Inline mode - mount directly in container
      if (!this.containerElement) {
        throw new Error('Container element is required for inline mode');
      }

      // Clear container
      this.containerElement.innerHTML = '';

      // Set container styles for seamless integration
      if (this.containerElement instanceof HTMLElement) {
        const currentStyle = this.containerElement.getAttribute('style') || '';
        if (!currentStyle.includes('min-height')) {
          this.containerElement.style.minHeight = `${DIMENSIONS.MIN_HEIGHT}px`;
        }
        if (!currentStyle.includes('position')) {
          this.containerElement.style.position = 'relative';
        }
        if (!currentStyle.includes('overflow')) {
          this.containerElement.style.overflow = 'hidden';
        }
      }

      // Create iframe
      this.iframe = document.createElement('iframe');
      this.iframe.src = iframeSrc;
      this.iframe.style.cssText = getInlineIframeStyles(
        this.config.config?.style?.fillParent || false
      );
      this.iframe.setAttribute('allow', 'payment');

      // Append to container
      this.containerElement.appendChild(this.iframe);

      // Show loader
      this.loaderManager = new LoaderManager(this.containerElement, {
        fillParent: this.config.config?.style?.fillParent,
      });
      this.loaderManager.show(this.iframe);
    }
  }

  /**
   * Add message listener for communication with iframe
   */
  private addMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      const allowedOrigin = new URL(this.iframeUrl).origin;
      const isExactMatch = event.origin === allowedOrigin;

      let isAllowedOrigin = isExactMatch;

      if (!isAllowedOrigin) {
        if (this.environment === 'sandbox' || this.environment === 'development') {
          const isLocalhostDev =
            (event.origin.includes('localhost') || event.origin.includes('127.0.0.1')) &&
            (allowedOrigin.includes('localhost') || allowedOrigin.includes('127.0.0.1'));
          isAllowedOrigin = isLocalhostDev;
        }

        if (!isAllowedOrigin) {
          const isAllowedApiOrigin =
            event.origin === 'https://dev.iframe.inflowpay.com' ||
            event.origin === 'https://pre-prod.iframe.inflowpay.xyz' ||
            event.origin === 'https://iframe.inflowpay.xyz';
          isAllowedOrigin = isAllowedApiOrigin;
        }
      }

      if (!isAllowedOrigin) {
        if (this.config.debug) {
          console.warn('[SDK] Rejected message from unauthorized origin:', event.origin);
        }
        return;
      }

      const data = event.data as IframeMessage;

      if (!data || !data.type) {
        return;
      }

      switch (data.type) {
        case 'iframe-ready':
          // Wait for iframe's javascript to be ready before sending config
          if (this.loaderManager) {
            this.loaderManager.hide(this.iframe || undefined);
          }
          this.sendConfigToIframe();
          break;

        case 'content-height':
          // Adjust iframe height based on content
          if (data.height && this.iframe) {
            const height = Math.max(data.height, DIMENSIONS.MIN_HEIGHT);
            this.iframe.style.height = `${height}px`;
            if (this.containerElement) {
              this.containerElement.style.minHeight = `${height}px`;
            }
          }
          break;

        case 'close':
          this.close();
          break;

        case 'success':
          if (this.config.onSuccess) {
            this.config.onSuccess(data.data);
          }
          break;

        case 'error':
          if (this.config.onError) {
            this.config.onError(data.data);
          }
          break;

        case '3ds-required':
          // Iframe requests SDK to open 3DS popup
          if (this.config.debug) {
            console.log('[SDK] Received 3DS request:', data.threeDsSessionUrl);
            console.log('[SDK] Session ID:', data.sessionId);
          }
          if (data.threeDsSessionUrl) {
            const sessionId = data.sessionId;
            const paymentId = data.paymentId || this.config.config?.paymentId || '';

            if (!data.sessionId) {
              console.warn('[SDK] No session ID provided by iframe');
            }

            if (this.config.debug) {
              console.log('[SDK] Opening 3DS modal with session ID:', sessionId);
            }

            this.modalManager.open(data.threeDsSessionUrl, sessionId as string, paymentId).then((success) => {
              if (this.config.debug) {
                console.log('[SDK] 3DS modal closed, result:', success);
              }
              // No longer send message back to iframe - unidirectional flow
            });
          } else {
            if (this.config.debug) {
              console.error('[SDK] 3DS required but no threeDsSessionUrl provided');
            }
          }
          break;

        case '3ds-success':
          // 3DS challenge completed via WebSocket, close modal automatically
          if (this.config.debug) {
            console.log('[SDK] 3DS completed, closing modal...');
            console.log('[SDK] Session ID from webhook:', data.sessionId);
          }
          // Pass session ID to validate before closing
          ModalManager.close(data.sessionId);
          break;

        case '3ds-failed':
          // 3DS challenge completed via WebSocket, close modal automatically
          if (this.config.debug) {
            console.log('[SDK] 3DS failed, closing modal...');
            console.log('[SDK] Session ID from webhook:', data.sessionId);
          }
          // Pass session ID to validate before closing
          ModalManager.close(data.sessionId);
          break;

        default:
          if (this.config.debug) {
            console.log('SDK: Received message:', data);
          }


      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Send configuration to the iframe
   */
  private sendConfigToIframe(): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      // Wait for iframe to load
      if (this.iframe) {
        this.iframe.onload = () => {
          this.sendConfigToIframe();
        };
      }
      return;
    }

    const message: IframeMessage = {
      type: 'sdkData',
      config: {
        ...(this.config.config || {}),
        paymentId: this.config.config?.paymentId
      },
      data: {
        publicKey: this.config.publicKey,
      },
    };

    const targetOrigin = this.getTargetOrigin();
    this.iframe.contentWindow.postMessage(message, targetOrigin);
  }


  /**
   * Close the iframe and cleanup
   */
  private close(): void {
    if (this.config.onClose) {
      this.config.onClose();
    }

    // Hide loader
    if (this.loaderManager) {
      this.loaderManager.hide(this.iframe || undefined);
      this.loaderManager = null;
    }

    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.usePopup) {
      // Remove overlay
      OverlayManager.removeOverlay();
    } else {
      // Clear container
      if (this.containerElement) {
        this.containerElement.innerHTML = '';
      }
    }

    this.iframe = null;
  }

  /**
   * Handle success state: when showDefaultSuccessUI is true, iframe shows success UI
   * and we keep it mounted; when false, we unmount so the parent can render their own.
   */
  public switchToSuccessState(_result: TransactionData): void {
    // Hide loader and remove message listener (same as close)
    if (this.loaderManager) {
      this.loaderManager.hide(this.iframe || undefined);
      this.loaderManager = null;
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.usePopup) {
      OverlayManager.removeOverlay();
      this.iframe = null;
      return;
    }

    const showDefault = this.config.config?.showDefaultSuccessUI ?? true;

    if (!showDefault && this.containerElement) {
      // Parent will render their own success UI: unmount iframe
      this.containerElement.innerHTML = '';
    }
    if (!showDefault) {
      this.iframe = null;
    }
    // When showDefault is true, keep iframe mounted (success UI is shown inside iframe)
  }

  /**
   * Get target origin for postMessage based on environment
   * In production/pre-prod: use exact origin for security
   * In dev/sandbox: use wildcard for development flexibility
   */
  private getTargetOrigin(): string {
    if (this.environment === 'production' || this.environment === 'preprod') {
      return new URL(this.iframeUrl).origin;
    }
    return '*';
  }

  /**
   * Public method to close the iframe
   */
  public destroy(): void {
    this.close();
  }
}

